<template>
  <div v-if="hasData" style="height: 150px">
    <e-chart :options="chartOptions" style="height: 135px" autoresize />
    <b-container>
      <b-row class="justify-content-md-center">
        <b-col v-for="[category, color] in getTopCatagories"
          :key="`${category}-${color}`"
          md="auto"
          class="d-flex align-items-center"
        >
        <div class="color-box" :style="{ backgroundColor: color }"></div>{{ category }}
        </b-col>
      </b-row>
    </b-container>
  </div>
  <div v-else-if="loaded" class="d-flex justify-content-center p-2 mt-1">No Data Available</div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>
<script>
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import asnPaletteShade from '@/xvisor/constants/asnColorPallete';
import colorPalatte from '@/xvisor/constants/colorPalette';
import granularity from '@/xvisor/utilities/granularity';
import processPaletteShade from '@/xvisor/constants/processColorPalette';
import themeStyle from '@/xvisor/utilities/themeStyle';

// Index for heatmap data: [x, y, _, heatmapObject].
const HEATMAPOBJECT = 3;
const DOMAIN = 'domain';

// Generate random hexidecimal string for the color.
const generateRandomColor = () => {
  const getBrightColor = () => Math.floor(Math.random() * 128) + 128;
  const red = getBrightColor().toString(16).padStart(2, '0');
  const green = getBrightColor().toString(16).padStart(2, '0');
  const blue = getBrightColor().toString(16).padStart(2, '0');

  return `#${red}${green}${blue}`;
};

// Removes 'exe' to make sure mappings to colors works for both Windows and Linux.
const removeExeExtension = (string) => {
  if (string.endsWith('.exe')) {
    return string.slice(0, -4);
  }
  return string;
};

// A closure for pre-selected colors and already selected colors.
const createColorPicker = () => {
  const colorMap = { ...asnPaletteShade, ...processPaletteShade };
  return (value) => {
    if (colorMap[value]) {
      return colorMap[value];
    }

    let randomColor = generateRandomColor();
    while (randomColor in colorMap) {
      randomColor = generateRandomColor();
    }
    colorMap[value] = randomColor;
    return randomColor;
  };
};

// Initialize closure.
const pickColor = createColorPicker();

export default {
  components: {
    SpinnerCmpt,
  },
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    alertUrl: {
      type: String,
      required: true,
    },
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
  },
  data() {
    return {
      series: [],
      alertSeries: [],
      maxLimit: 4,
      loaded: false,
    };
  },
  mounted() {
    this.httpGet();
  },
  computed: {
    hasData() {
      return this.loaded && this.series.length > 0;
    },
    getTopCatagories() {
      const catagoriesMap = new Map();
      this.series.forEach((item) => {
        const {
          entity,
          value,
          metadata,
        } = item;
        switch (entity) {
          case DOMAIN: {
            const legendValue = metadata.org;
            if (legendValue !== undefined && legendValue !== 'none') {
              if (catagoriesMap.has(legendValue)) {
                catagoriesMap.set(
                  legendValue,
                  [catagoriesMap.get(legendValue)[0], catagoriesMap.get(legendValue)[1] + 1],
                );
              } else {
                catagoriesMap.set(legendValue, [metadata.asn, 1]);
              }
            }
            break;
          }
          default: {
            const legendValue = removeExeExtension(value);
            if (legendValue !== undefined && legendValue !== 'none') {
              if (catagoriesMap.has(legendValue)) {
                catagoriesMap.set(
                  legendValue,
                  [catagoriesMap.get(legendValue)[0], catagoriesMap.get(legendValue)[1] + 1],
                );
              } else {
                catagoriesMap.set(legendValue, [legendValue, 1]);
              }
            }
          }
        }
      });
      const topCatagories = [];
      catagoriesMap.forEach((value, key) => {
        topCatagories.push([key, ...value]);
      });

      topCatagories.sort((first, second) => second[2] - first[2]);
      return topCatagories.slice(0, this.maxLimit).map(
        (catagoriesArray) => [catagoriesArray[0], pickColor(catagoriesArray[1])],
      );
    },
    formatData() {
      return this.series.map((item, idx) => {
        const {
          count,
          entity,
          timestamp,
          value,
          metadata,
        } = item;
        const formatedTimestamp = moment.utc(timestamp).local().format('MMM DD, h:mm A');
        const xAxis = Math.floor(idx / 5);
        const yAxis = 5 - (idx % 5) - 1;
        switch (entity) {
          case DOMAIN: {
            let alerts = {};
            if (this.alertSeries.length !== 0) {
              alerts = this.alertSeries[idx].alerts;
            }
            const heatmapObj = {
              entity,
              metadata,
              alerts,
              valueToColor: metadata.asn,
              timestamp: formatedTimestamp,
              values: value,
              counts: count,
            };
            return [xAxis, yAxis, 0, heatmapObj];
          }
          default: {
            let objectValue = [];
            let countValue = [];
            if (value) {
              objectValue = [value];
              countValue = [count];
            }
            let alerts = {};
            if (this.alertSeries.length !== 0) {
              alerts = this.alertSeries[idx].alerts;
            }
            const valueToColor = removeExeExtension(value);
            const heatmapObj = {
              entity,
              metadata,
              valueToColor,
              alerts,
              timestamp: formatedTimestamp,
              values: objectValue,
              counts: countValue,
            };
            return [xAxis, yAxis, 0, heatmapObj];
          }
        }
      });
    },
    chartOptions() {
      return {
        grid: {
          show: true,
          height: '90vh',
          top: 30,
          left: 10, // change this back to 15
          right: 5,
        },
        xAxis: {
          type: 'category',
          name: 'Time',
          boundaryGap: true,
          axisLabel: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitArea: {
            show: false,
          },
        },
        yAxis: {
          type: 'category',
          data: [0, 1, 2, 3, 4],
          axisLabel: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          splitArea: {
            show: false,
          },
        },
        series: [
          {
            name: 'Data',
            type: 'heatmap',
            data: this.formatData,
            itemStyle: {
              borderColor: themeStyle.styleToggle(colorPalatte.white, colorPalatte.blueLightBackground),
              borderWidth: 2,
              color: (params) => {
                const heatmapObj = params.value[HEATMAPOBJECT];
                if (heatmapObj.valueToColor === undefined) {
                  return themeStyle.styleToggle(colorPalatte.blueLightBackground, colorPalatte.white);
                }
                if (Object.entries(heatmapObj.alerts).length > 0) {
                  return colorPalatte.red;
                }
                return pickColor(heatmapObj.valueToColor);
              },
            },
          },
        ],
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            const { data } = params;

            const {
              timestamp, entity, values, counts, metadata, alerts,
            } = data[HEATMAPOBJECT];

            const hasCounts = counts.length > 0 && counts[0] > 0;

            if (!hasCounts && Object.entries(alerts).length === 0 && Object.keys(metadata).length === 0) {
              return `
                <div class="apache-echarts-tooltop">
                  ${timestamp} <br>
                  No User Activity
                </div>
              `;
            }

            const alertDisplay = Object.entries(alerts).length > 0 && hasCounts
              ? `Alerts:<br>${
                Object.entries(alerts).map(
                  ([alertType, alertCount]) => `${alertType} (Count: ${alertCount})<br>`,
                ).join('')
              }`
              : '';

            const valueDisplay = this.createTopEntityToolmap(entity, values, counts);

            const metadataDetails = entity === DOMAIN && metadata.org && metadata.asn
              ? `Top Destination: ${metadata.org} (ASN: ${metadata.asn})`
              : '';

            const countDisplay = entity !== DOMAIN && hasCounts
              ? `Count: ${counts[0]} <br>`
              : '';

            return `
              <div class="apache-echarts-tooltop">
                ${timestamp} <br>
                ${alertDisplay}
                ${valueDisplay} <br>
                ${metadataDetails}
                ${countDisplay}
              </div>
            `;
          },
        },
      };
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;

      const alertRequest = this.$http.get(this.alertUrl, {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity72(this.timeRange),
        },
      })
        .catch(() => ({ data: [] }));

      const dataRequest = this.$http.get(this.url, {
        params: {
          start: this.timeRange.start.toISOString(),
          end: this.timeRange.end.toISOString(),
          stepMinutes: granularity.granularity72(this.timeRange),
        },
      });

      Promise.all([alertRequest, dataRequest])
        .then(([alertReponse, dataResponse]) => {
          this.alertSeries = alertReponse.data;
          this.series = dataResponse.data;
        })
        .finally(() => { this.loaded = true; });
    },
    capitalizeString(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    createTopEntityToolmap(entity, values, counts) {
      switch (entity) {
        case DOMAIN: {
          if (counts.length === 0 || counts[0] === 0) {
            return 'No Domain Names Detected';
          }
          const valuesWithCount = values.map((value, index) => `${value} (Count: ${counts[index]})`);
          return `Top ${this.capitalizeString(entity)}(s): <br> ${valuesWithCount.join('<br>')}`;
        }
        default: {
          return (counts.length > 0 && counts[0] > 0)
            ? `Top ${this.capitalizeString(entity)}: ${values[0]}`
            : 'No User Activity';
        }
      }
    },
  },
};
</script>

<style scoped>
.color-box {
  display: inline-block;
  width: 15px;
  height: 15px;
  margin-right: 5px;
}
</style>
